import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useBomChildren } from './useBomChildren';
import DiscussMenu from '../../components/DiscussMenu';
import { decodeContext, registerViewState } from '../../utils/collab';
import BomRow from './BomRow';
import ExportModal from './ExportModal';
import EcoModal from './EcoModal';
import CompareModal from './CompareModal';
import CategoryModal from './CategoryModal';
import ColumnPicker from './ColumnPicker';
import Spinner from '../ui/Spinner';
import { useToast } from '../ui/Toast';
import {
  computeCostRollup,
  findUnitCostField,
  COST_COLUMN_KEYS,
  UNIT_COST_KEY,
  COST_KEY,
  TOTAL_COST_KEY,
} from '../utils/cost';
import {
  normalizeColKey,
  findCategoryColumn,
  categoryOf,
  mergeRowColumns,
} from '../utils/columns';
import { searchIdDigits } from '../api/orcanosClient';
import { FILE_KEY } from './exportUtils';
import { getBomUser, getSettings, bomStructure } from '../settings/settingsStore';
import './BomTree.css';

// Compute hierarchical level numbers for every node in tree order.
// Parent nodes appear before their children in the flat list, so a single
// pass is enough — the counter per parent is always ready when needed.
function computeLevelNumbers(nodes) {
  const nums = new Map();
  const counters = new Map();
  // Root nodeKeys — children of roots restart numbering at 1 without chaining
  // the root's own number as a prefix. So root children are "1", "2", "3";
  // their children are "1.1", "1.2", etc.
  const rootKeys = new Set(nodes.filter((n) => n.isRoot).map((n) => n.nodeKey));

  for (const n of nodes) {
    const pk = n.parentKey ?? '';
    const idx = (counters.get(pk) ?? 0) + 1;
    counters.set(pk, idx);
    const parentIsRoot = n.parentKey ? rootKeys.has(n.parentKey) : true;
    const parentNum = !parentIsRoot ? nums.get(n.parentKey) : null;
    nums.set(n.nodeKey, parentNum ? `${parentNum}.${idx}` : String(idx));
  }
  return nums;
}

// Keep the search debounced so typing doesn't re-render on every keystroke.
function useDebounced(value, delay = 200) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

// Columns visible by default (when "Show additional fields" is OFF).
// Matched against both the normalized column key (Name) and title (Title).
// The synthetic __revision column is always included; __quantity is additional.
const DEFAULT_VISIBLE_COLS = new Set([
  'key', 'name', 'status', 'part status', 'routing state', 'revision', 'quantity',
  'unit cost', 'cost', 'total cost',
  // The ECO view's synthetic File column (`__file` normalises to "file").
  'file',
]);

// Columns we never display. The data is still kept on each row — we just
// don't add them to the rendered column list. Match by either Name or Title,
// case-insensitive, with spaces and underscores treated equivalently.
const HIDDEN_COLUMN_KEYS = new Set([
  'copy as link',
  'in pool',
  'is branch',
  'original id',
  'id',
  'master part source',
  // Hide the dynamic Quantity column from the PRT row — we render Quantity
  // via the synthetic `__quantity` column so it shows for both PRTs ("1")
  // and PIs (their Quantity field).
  'quantity',
  // The PRT filter exposes "Revision" and the PI filter exposes "Part Revision".
  // We hide both and add a single synthetic `__revision` column that pulls from
  // whichever the row has.
  'revision',
  'part revision',
]);

// ─── Feature flag: "Expand by category" ────────────────────────────────────
// Built and working, but held back for now. Flip to true to restore the ⋯ menu
// item on root rows — everything behind it (CategoryModal, findCategoryColumn,
// tree.getChildRows, expandAll's firstLevelFilter) stays wired up and tested.
const EXPAND_BY_CATEGORY_ENABLED = false;

// ─── Visible-column preference ─────────────────────────────────────────────
// The column chooser's selection is persisted per user AND per view (BOMs and
// Part Catalog come from different filters, so their column sets differ).
// Stored as a plain array of column keys — no credentials, no row data.
function columnPrefKey(view) {
  return `bom_cols_${getBomUser()}_${view}`;
}

function loadColumnPref(view) {
  try {
    const saved = JSON.parse(localStorage.getItem(columnPrefKey(view)) || 'null');
    return Array.isArray(saved) && saved.length ? new Set(saved) : null;
  } catch {
    return null;
  }
}


export default function BomTree({
  onAuthExpired,
  view = 'boms',
  topLabel = 'BOMs',
  // Where-Used wiring. When set, the tree renders the BOMs containing this part id.
  topWhereUsedId = null,
  targetOriginalId = null,
  // Integer used by "Locate" to find the matching PI inside an expanded BOM.
  //   PRT click → the part's own id.
  //   PI  click → the PI's CS21 integer (source PRT's id).
  // Match: descendant.row.cs21Int === targetCs21Int.
  targetCs21Int = '',
  whereUsedLabel = '',
  onExitWhereUsed,
  onWhereUsed,
}) {
  const [search, setSearch] = useState('');
  // 350ms debounce — slightly longer than client-only filtering since each
  // change now triggers a server roundtrip.
  const debounced = useDebounced(search, 350);

  // ─── Search history (per user, persisted in localStorage) ────
  function historyKey() {
    return `bom_search_history_${getBomUser()}`;
  }
  const [searchHistory, setSearchHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem(historyKey()) || '[]'); }
    catch { return []; }
  });
  const [historyOpen, setHistoryOpen] = useState(false);

  // Persist a new search string (deduped, capped at 10, newest first).
  useEffect(() => {
    const q = debounced.trim();
    if (!q) return;
    setSearchHistory((prev) => {
      const next = [q, ...prev.filter((s) => s !== q)].slice(0, 10);
      try { localStorage.setItem(historyKey(), JSON.stringify(next)); } catch {}
      return next;
    });
  }, [debounced]);

  const applyHistory = useCallback((s) => {
    setSearch(s);
    setHistoryOpen(false);
  }, []);
  const tree = useBomChildren({
    onAuthExpired,
    topView: view,
    topSearchQuery: debounced,
    topWhereUsedId,
  });

  // Per-root progress for "Expand all" — { rootUid: "12 / 47" or null }.
  const [progress, setProgress] = useState({});

  // Where-Used "Locate" state. `locatingKey` is the rootKey currently being
  // expanded; `locatedKey` is the descendant that should briefly highlight.
  const [locatingKey, setLocatingKey] = useState(null);
  const [locatedKey, setLocatedKey] = useState(null);

  // Export modal — set to the node being exported when open, null otherwise.
  // Any row can be the export root, not just a top-level BOM.
  const [exportNode, setExportNode] = useState(null);
  // "Expand by category" — { node, col, options } while the picker is open.
  const [categoryPicker, setCategoryPicker] = useState(null);
  // nodeKey whose children are being fetched to build that picker.
  const [categoryLoadingKey, setCategoryLoadingKey] = useState(null);
  // Related ECOs modal — set to the node whose ECOs to display, null otherwise.
  const [ecoNode, setEcoNode] = useState(null);
  // Compare Revisions modal — the node whose change log to diff, null otherwise.
  const [compareNode, setCompareNode] = useState(null);

  // "Discuss with…" — { x, y, context } while the people menu is open.
  const [discussAt, setDiscussAt] = useState(null);

  const { showToast } = useToast();

  // Level numbers: "1", "1.2", "1.2.3", … computed from the full loaded list
  // (not visibleNodes) so numbers are stable regardless of expand state.
  const levelNumbers = useMemo(() => computeLevelNumbers(tree.nodes), [tree.nodes]);

  // ─── Resolved (exact) sub-tree costs ──────────────────────────
  // `exactSubtree` holds only the sums the user explicitly asked for via the ∑
  // button in a Total Cost cell. Keyed by part id, so resolving one occurrence
  // answers every other occurrence of the same sub-assembly. A row that isn't
  // complete never renders a number at all (see BomRow), so a partly-loaded
  // tree can't produce a misleading figure.
  const [exactSubtree, setExactSubtree] = useState(() => new Map());
  const [calcKeys, setCalcKeys] = useState(() => new Set());

  // A refresh, a new search or a new page invalidates resolved totals along
  // with the rows they were computed from.
  const resetExact = useCallback(() => {
    setExactSubtree(new Map());
    setCalcKeys(new Set());
  }, []);

  // Re-runs on mount AND whenever `loadTop`'s identity changes — which
  // happens when the view, search or where-used target changes (see hook's useCallback
  // deps). So changing the search box re-fetches page 1 with the new query.
  useEffect(() => {
    resetExact();
    tree.loadTop(1);
  }, [tree.loadTop, resetExact]);

  // ─── Column derivation ────────────────────────────────────────
  // Columns are the UNION of every loaded row's Field array (each row's own
  // fields in Web_order), roots first so a single-type tenant's column order
  // is exactly what it always was. Under the assembly structure one expand
  // returns rows of up to three work item types — the sub-assembly type, PRT
  // parts and DMS items — and reading the first root alone left every field a
  // child type does not share with it with no column at all. Fields that match
  // by Name OR Title become ONE column carrying both aliases (see
  // mergeRowColumns), so "Name" or "Status" renders for a part and a DMS item
  // alike while each type's own fields stay available in the column chooser.
  const columns = useMemo(() => {
    const firstRoot = tree.nodes.find((n) => n.depth === 0);
    if (!firstRoot) return null;

    // The raw cost field is re-published as the synthetic `__unitCost` column
    // so the header reads "Unit Cost" — unambiguous next to the computed
    // "Cost" (qty × unit) and "Total Cost" (rolled up) columns. Child rows come
    // from a different filter than roots, so if the root has no cost field we
    // look at the first child before concluding there is no cost data at all.
    const costField =
      findUnitCostField(firstRoot.row.fields) ||
      findUnitCostField(tree.nodes.find((n) => n.depth > 0)?.row?.fields);
    const costFieldName = costField ? normalizeColKey(costField.Name) : null;
    const costFieldTitle = costField ? normalizeColKey(costField.Title) : null;

    const fieldArrays = [
      firstRoot.row.fields,
      ...tree.nodes.filter((n) => n !== firstRoot).map((n) => n.row.fields),
    ];
    const dynamic = mergeRowColumns(fieldArrays, {
      skip: (n, t) => {
        // Empty-guarded: a field with no Title must not match a cost field
        // that also has no Title.
        if ((costFieldName && n === costFieldName) || (costFieldTitle && t === costFieldTitle)) {
          return true;
        }
        return HIDDEN_COLUMN_KEYS.has(n) || HIDDEN_COLUMN_KEYS.has(t);
      },
    });
    const defaultCols = dynamic.filter((c) => {
      const n = normalizeColKey(c.key);
      const t = normalizeColKey(c.title);
      return DEFAULT_VISIBLE_COLS.has(n) || DEFAULT_VISIBLE_COLS.has(t);
    });
    const additionalCols = dynamic.filter((c) => {
      const n = normalizeColKey(c.key);
      const t = normalizeColKey(c.title);
      return !DEFAULT_VISIBLE_COLS.has(n) && !DEFAULT_VISIBLE_COLS.has(t);
    });
    // Additional fields always go after Revision so toggling them doesn't
    // reorder the core columns. Quantity is a BOM notion; File exists only for an ECO's
    // DMS items.
    const isBomView = view === 'boms' || view === 'parts';
    return [
      ...defaultCols,
      ...(isBomView ? [{ key: '__quantity', title: 'Quantity' }] : []),
      { key: '__revision', title: 'Revision' },
      ...(view === 'ecos' ? [{ key: FILE_KEY, title: 'File' }] : []),
      ...(costField
        ? [
            { key: UNIT_COST_KEY, title: 'Unit Cost' },
            { key: COST_KEY, title: 'Cost' },
            { key: TOTAL_COST_KEY, title: 'Total Cost' },
          ]
        : []),
      ...additionalCols,
    ];
  }, [tree.nodes, view]);

  // ─── Cost rollup ──────────────────────────────────────────────
  // One O(rows) backwards pass over the loaded rows — no fetching, no extra
  // renders. Recomputes only when the node list itself changes (expand,
  // collapse, page change), which is already a full re-render anyway.
  const costs = useMemo(
    () => computeCostRollup(tree.nodes, { exactSubtree }),
    [tree.nodes, exactSubtree]
  );

  async function handleCalcTotal(node) {
    const partId = node.row.originalId;
    if (!partId) {
      showToast('Cannot total this row — it has no part ID.', 'error');
      return;
    }
    setCalcKeys((prev) => new Set(prev).add(node.nodeKey));
    try {
      const result = await tree.resolveSubtreeCost(node);
      // null means the walk failed and already toasted — leave the cell as it
      // was rather than showing a total we can't stand behind.
      if (!result) return;
      // Merge EVERY part the walk settled, not just the clicked row. The walk
      // had to compute all of them, so expanding this branch afterwards shows
      // its sub-assemblies already totalled instead of another column of ∑.
      setExactSubtree((prev) => {
        const next = new Map(prev);
        for (const [id, perUnit] of result.resolved) next.set(id, perUnit);
        return next;
      });
    } finally {
      setCalcKeys((prev) => {
        const next = new Set(prev);
        next.delete(node.nodeKey);
        return next;
      });
    }
  }

  // ─── Column chooser ───────────────────────────────────────────
  // `visibleKeys` is the user's explicit selection (a Set of column keys),
  // or null when they haven't chosen — in which case the core columns listed
  // in DEFAULT_VISIBLE_COLS are shown, which is what the grid has always done.
  const [visibleKeys, setVisibleKeys] = useState(() => loadColumnPref(view));

  const defaultKeys = useMemo(() => {
    if (!columns) return [];
    return columns
      .filter((c) => {
        const n = normalizeColKey(c.key);
        const t = normalizeColKey(c.title);
        return DEFAULT_VISIBLE_COLS.has(n) || DEFAULT_VISIBLE_COLS.has(t);
      })
      .map((c) => c.key);
  }, [columns]);

  const changeVisibleKeys = useCallback((next) => {
    setVisibleKeys(next);
    try {
      localStorage.setItem(columnPrefKey(view), JSON.stringify([...next]));
    } catch { /* storage unavailable — the choice just won't survive a reload */ }
  }, [view]);

  // `displayColumns` is the filtered view used for rendering.
  // `columns` (full set) is what the export modal offers, so an export can
  // still include fields that are hidden on screen.
  const displayColumns = useMemo(() => {
    if (!columns) return null;
    // A saved selection can name columns this filter doesn't return (the pref
    // outlives a filter change). Intersect, and fall back to the defaults if
    // nothing survives.
    const chosen = visibleKeys
      ? columns.filter((c) => visibleKeys.has(c.key))
      : [];
    if (chosen.length) return chosen;
    const byDefault = new Set(defaultKeys);
    const defaults = columns.filter((c) => byDefault.has(c.key));
    // A DMS or ECO filter may name its fields nothing the BOM defaults know — show its first
    // few rather than a grid with only the Tree column.
    return defaults.length > 1 ? defaults : columns.slice(0, 8);
  }, [columns, visibleKeys, defaultKeys]);

  // What the picker shows as ticked — the keys actually being rendered.
  const pickerSelection = useMemo(
    () => new Set((displayColumns || []).map((c) => c.key)),
    [displayColumns]
  );

  // ─── Search filter ────────────────────────────────────────────
  // A node is visible if:
  //   - debounced is empty, OR
  //   - the node matches: Obj_name or Key contains the query, or — for a
  //     key/id-shaped query — its id (or, for a PI, its master part's id)
  //     contains the digits (same rule as the server clause), OR
  //   - any descendant matches (so the chain stays visible), OR
  //   - any ancestor matches (so children of a matched parent stay visible).
  //
  // We compute this once by tagging each node with its match state and then
  // walking the flat list once more with parent context.
  const visibleSet = useMemo(() => {
    if (!debounced.trim()) return null;
    const q = debounced.trim().toLowerCase();
    const digits = searchIdDigits(q);
    const nodes = tree.nodes;

    const matches = (row) =>
      (row.objName || '').toLowerCase().includes(q) ||
      (row.userPrefix || '').toLowerCase().includes(q) ||
      (!!digits &&
        (String(row.itemId || '').includes(digits) ||
          String(row.originalId || '').includes(digits)));

    const directMatch = new Set();
    nodes.forEach((n) => {
      if (matches(n.row)) {
        directMatch.add(n.nodeKey);
      }
    });

    // Mark descendants of any match (search-includes-children) AND mark every
    // ancestor of any match (so the tree path is preserved).
    const visible = new Set(directMatch);

    // Descendants: scan top-down, maintain a stack of "currently inside a match".
    const insideMatchStack = [];
    nodes.forEach((n) => {
      while (
        insideMatchStack.length &&
        insideMatchStack[insideMatchStack.length - 1].depth >= n.depth
      ) {
        insideMatchStack.pop();
      }
      if (directMatch.has(n.nodeKey)) {
        insideMatchStack.push(n);
      }
      if (insideMatchStack.length > 0) {
        visible.add(n.nodeKey);
      }
    });

    // Ancestors: scan bottom-up, when we see a visible node, add its ancestors.
    const needsAncestorAtDepth = new Set();
    for (let i = nodes.length - 1; i >= 0; i--) {
      const n = nodes[i];
      if (visible.has(n.nodeKey)) {
        if (n.depth > 0) needsAncestorAtDepth.add(n.depth - 1);
      }
      if (needsAncestorAtDepth.has(n.depth)) {
        visible.add(n.nodeKey);
        needsAncestorAtDepth.delete(n.depth);
        if (n.depth > 0) needsAncestorAtDepth.add(n.depth - 1);
      }
    }
    return visible;
  }, [debounced, tree.nodes]);

  const visibleNodes = useMemo(() => {
    if (!visibleSet) return tree.nodes;
    return tree.nodes.filter((n) => visibleSet.has(n.nodeKey));
  }, [tree.nodes, visibleSet]);

  // ─── Handlers ─────────────────────────────────────────────────
  // Click "Locate" on a where-used root: level-order BFS — fully expand the
  // current level, scan it for the target, stop on first match, otherwise
  // descend. The match value is the same one we passed to
  // dbo.fn_GetRootParentByCS21() when entering Where Used (`targetOriginalId`).
  // The descendant matches when its `cs21Raw` or `cs21Int` equals that value.
  async function handleLocate(rootNode) {
    const target = String(targetOriginalId || '').trim();
    if (!target) {
      showToast('Cannot locate: no source ID for this item.', 'error');
      return;
    }
    setLocatingKey(rootNode.nodeKey);
    try {
      const foundKey = await tree.findAndExpand(rootNode, { target });
      if (!foundKey) {
        showToast('Item not found inside this BOM.', 'error');
        return;
      }
      // Persistent highlight — overwrite if a previous Locate had set one.
      setLocatedKey(foundKey);
      // Defer to the next paint so React has rendered the row before we scroll.
      requestAnimationFrame(() => {
        const el = document.querySelector(
          `tr[data-node-key="${foundKey}"]`
        );
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      });
    } finally {
      setLocatingKey(null);
    }
  }

  // ─── Sharing a row with a colleague ───────────────────────────
  // ⚠️ Critical Note #79. A BOM row has no panel behind it: the whole grid is one
  // account-wide module, so the card the colleague receives carries NO panel id, and the
  // only thing standing between it and someone who should not see BOM data is the MODULE
  // licence, re-checked server-side when the card is read.
  //
  // What travels is ids, never a title (Critical Note #71): the view, the row's own item
  // id and key, and — crucially — its ROOT. This grid loads roots from the server and
  // children only on expand, so a child row cannot be reached at all without being told
  // which top-level item to open first.

  /** The top-level ancestor of a row (itself, for a root). */
  const rootOf = useCallback((node) => {
    const byKey = new Map(tree.nodes.map((n) => [n.nodeKey, n]));
    let cur = node;
    let hops = 0;
    while (cur && cur.depth > 0 && cur.parentKey && hops < 64) {
      cur = byKey.get(cur.parentKey);
      hops += 1;
    }
    return cur || node;
  }, [tree.nodes]);

  const handleDiscuss = useCallback((e, node) => {
    e.preventDefault();
    const root = rootOf(node);
    const rootKey = String(root.row.userPrefix || root.row.itemId || '').trim();
    const ownKey = String(node.row.userPrefix || node.row.itemId || '').trim();
    setDiscussAt({
      x: e.clientX,
      y: e.clientY,
      context: {
        kind: 'row',
        module: 'bom',
        // Where-Used is a transient pane, not a route — a row shared from inside it points
        // at the view it came from, which is a place the reader can actually be taken to.
        route: `/bom/${view}`,
        panel_id: '',
        state: { view, search: rootKey },
        focus: {
          key: ownKey,
          item_id: String(node.row.itemId || ''),
          cs21: String(node.row.cs21Int || ''),
          root_key: rootKey,
          root_id: String(root.row.itemId || ''),
        },
        label: `${topLabel} · ${node.row.objName || ownKey}`.slice(0, 200),
      },
    });
  }, [rootOf, view, topLabel]);

  /** What "Share this view" in the chat dock attaches while a BOM view is on screen. */
  useEffect(() => {
    if (topWhereUsedId) return undefined;   // the Where-Used pane is not a route
    return registerViewState(() => ({ view, search: debounced, page: tree.topPage }));
  }, [view, debounced, tree.topPage, topWhereUsedId]);

  // ─── Opening what somebody shared ─────────────────────────────
  const [searchParams] = useSearchParams();
  const rawCtx = topWhereUsedId ? '' : (searchParams.get('ctx') || '');
  const appliedCtxRef = useRef('');
  const pendingPageRef = useRef(0);
  const resolvingRef = useRef(false);
  const triedRootRef = useRef('');
  // The query the rows ON SCREEN came from. A card that carries a search changes the search
  // box, and the reload that follows is a round trip — for that moment the visible rows are
  // still the PREVIOUS query's, and `topLoading` has not flipped yet either (the load starts
  // in an effect that dispatches after this one has already read it). Looking for the shared
  // row then found it in the list that was about to be replaced, expanded that copy, and
  // threw the whole thing away when the new rows landed.
  const loadedQueryRef = useRef('');
  const wasLoadingRef = useRef(false);
  // The row a card asked for, until it has been found or given up on.
  const [sharedFocus, setSharedFocus] = useState(null);

  useEffect(() => {
    if (wasLoadingRef.current && !tree.topLoading) loadedQueryRef.current = debounced;
    wasLoadingRef.current = tree.topLoading;
  }, [tree.topLoading]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!rawCtx || appliedCtxRef.current === rawCtx) return;
    const ctx = decodeContext(rawCtx);
    if (!ctx) return;
    appliedCtxRef.current = rawCtx;
    const st = ctx.state || {};
    const focus = ctx.focus || {};
    // A search re-pages to 1 on the server, so a page number only means anything when the
    // card carried no search of its own.
    if (st.search) setSearch(String(st.search));
    else if (Number(st.page) > 1) pendingPageRef.current = Number(st.page);
    if (focus.key || focus.item_id) {
      triedRootRef.current = '';
      setSharedFocus({
        key: String(focus.key || ''),
        itemId: String(focus.item_id || ''),
        cs21: String(focus.cs21 || ''),
        rootKey: String(focus.root_key || ''),
        rootId: String(focus.root_id || ''),
      });
    }
  }, [rawCtx]);

  useEffect(() => {
    if (!pendingPageRef.current || tree.topLoading) return;
    const want = pendingPageRef.current;
    pendingPageRef.current = 0;
    if (want !== tree.topPage) { resetExact(); tree.loadTop(want); }
  }, [tree.topLoading, tree.topPage, tree.loadTop, resetExact]);

  const scrollToNode = useCallback((nodeKey) => {
    requestAnimationFrame(() => {
      const el = document.querySelector(`tr[data-node-key="${nodeKey}"]`);
      if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  }, []);

  useEffect(() => {
    if (!sharedFocus || resolvingRef.current) return;
    // The search box was just set from the card; wait for the debounce and the fetch it
    // starts, or the row is looked for in the PREVIOUS list and reported missing.
    if (tree.topLoading || search !== debounced) return;
    if (loadedQueryRef.current !== debounced) return;

    const hit = tree.nodes.find((n) =>
      (sharedFocus.itemId && String(n.row.itemId || '') === sharedFocus.itemId)
      || (sharedFocus.key && String(n.row.userPrefix || '') === sharedFocus.key));
    if (hit) {
      setLocatedKey(hit.nodeKey);
      scrollToNode(hit.nodeKey);
      setSharedFocus(null);
      return;
    }

    // Not among the loaded rows — it is a child of one. Expand its root looking for it.
    const root = tree.nodes.find((n) => n.depth === 0 && (
      (sharedFocus.rootId && String(n.row.itemId || '') === sharedFocus.rootId)
      || (sharedFocus.rootKey && String(n.row.userPrefix || '') === sharedFocus.rootKey)));
    if (!root) {
      if (tree.nodes.length || tree.topError) {
        showToast('The shared item is not in this list any more.', 'error');
        setSharedFocus(null);
      }
      return;
    }
    if (triedRootRef.current === root.nodeKey) { setSharedFocus(null); return; }
    triedRootRef.current = root.nodeKey;
    resolvingRef.current = true;
    setLocatingKey(root.nodeKey);
    (async () => {
      try {
        const foundKey = await tree.findAndExpand(root, {
          target: sharedFocus.cs21,
          itemId: sharedFocus.itemId,
        });
        if (foundKey) {
          setLocatedKey(foundKey);
          scrollToNode(foundKey);
        } else {
          showToast('Could not find the shared item inside this BOM.', 'error');
        }
      } finally {
        resolvingRef.current = false;
        setLocatingKey(null);
        setSharedFocus(null);
      }
    })();
  }, [sharedFocus, tree.nodes, tree.topLoading, tree.topError, tree.findAndExpand,
      search, debounced, scrollToNode, showToast]);

  // ─── Expand by category ───────────────────────────────────────
  // Fetch the row's DIRECT children, group them by their Category value and
  // let the user pick which of those groups to descend into. Nothing is
  // hidden — unticked children are still attached, just left collapsed.
  async function handleExpandByCategory(node) {
    setCategoryLoadingKey(node.nodeKey);
    try {
      const rows = await tree.getChildRows(node);
      if (!rows) return; // fetch failed — the hook already toasted
      if (rows.length === 0) {
        showToast('This item has no children to expand.', 'error');
        return;
      }
      const col = findCategoryColumn(rows);
      if (!col) {
        showToast('This filter returns no Category field.', 'error');
        return;
      }
      const counts = new Map();
      for (const row of rows) {
        const label = categoryOf(row, col);
        counts.set(label, (counts.get(label) || 0) + 1);
      }
      const options = [...counts.entries()]
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setCategoryPicker({ node, col, options });
    } finally {
      setCategoryLoadingKey(null);
    }
  }

  function confirmExpandByCategory(selectedLabels) {
    const { node, col } = categoryPicker;
    setCategoryPicker(null);
    handleExpandAll(node, {
      firstLevelFilter: (row) => selectedLabels.has(categoryOf(row, col)),
    });
  }

  function handleExpandAll(node, opts) {
    setProgress((p) => ({ ...p, [node.nodeKey]: { done: 0, total: 1 } }));
    tree
      .expandAll(
        node,
        (done, total) =>
          setProgress((p) => ({ ...p, [node.nodeKey]: { done, total } })),
        opts
      )
      .finally(() => {
        // Keep the final number visible briefly, then clear.
        setTimeout(() => {
          setProgress((p) => {
            const next = { ...p };
            delete next[node.nodeKey];
            return next;
          });
        }, 1200);
      });
  }

  // ─── Render ───────────────────────────────────────────────────
  const inWhereUsed = !!targetOriginalId;
  // Where-used, Related ECOs and Compare Revisions read the parts project (PRT/PI); the DMS
  // and ECO lists come from other projects and item types, so they offer none of them.
  // 'assemblies' is the assembly work item's own top-level list — the same items that
  // appear inside a BOM, so it offers the same row actions.
  const bomActions = view === 'boms' || view === 'parts' || view === 'assemblies';
  // Where-used answers through dbo.fn_GetRootParentByCS21, which walks PART INSTANCES — under
  // the part hierarchy or the assembly structure it would list the wrong BOMs (or, with no
  // PRT BOM filter set, nothing at all), so it is not offered there. Every OTHER capability —
  // expand, expand-all, export, Related ECOs, Compare Revisions — works in all three.
  const noWhereUsed = bomStructure(getSettings()) !== 'instance';

  return (
    <div className="bom-tree">
      {inWhereUsed && (
        <div className="bom-where-used-banner">
          <button
            type="button"
            className="bom-where-used-back"
            onClick={onExitWhereUsed}
            aria-label="Back"
          >
            ← Back
          </button>
          <span className="bom-where-used-label">
            Where used:&nbsp;
            <strong>{whereUsedLabel || `#${targetOriginalId}`}</strong>
          </span>
        </div>
      )}
      <div className="bom-toolbar">
        <div className="bom-search">
          <span className="bom-search-icon">🔍</span>
          <input
            className="bom-search-input"
            type="text"
            placeholder={`Search ${topLabel} by name, code, key or ID…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setHistoryOpen(true)}
            onBlur={() => setHistoryOpen(false)}
            onKeyDown={(e) => { if (e.key === 'Escape') setHistoryOpen(false); }}
          />
          {search && (
            <button
              type="button"
              className="bom-search-clear"
              onClick={() => setSearch('')}
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
          {historyOpen && searchHistory.length > 0 && (
            <div className="bom-search-history" role="listbox">
              {searchHistory.map((s) => (
                <button
                  key={s}
                  type="button"
                  className="bom-search-history-item"
                  role="option"
                  onMouseDown={(e) => { e.preventDefault(); applyHistory(s); }}
                >
                  <span className="bom-search-history-icon">↩</span>
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          className="bom-refresh-btn"
          onClick={() => { resetExact(); tree.loadTop(1); }}
          disabled={tree.topLoading}
        >
          {tree.topLoading ? <Spinner size={14} /> : '⟳'}
          <span>Refresh</span>
        </button>
        {columns && columns.length > 0 && (
          <ColumnPicker
            columns={columns}
            selected={pickerSelection}
            onChange={changeVisibleKeys}
            defaultKeys={defaultKeys}
          />
        )}
      </div>

      {tree.topLoading && tree.nodes.length === 0 && (
        <div className="bom-empty">
          <Spinner size={20} />
          <p>Loading {topLabel}…</p>
        </div>
      )}

      {!tree.topLoading && tree.nodes.length === 0 && tree.topError && (
        <div className="bom-empty">
          <p className="bom-empty-error">Failed to load {topLabel}.</p>
          <p className="bom-empty-detail">{tree.topError}</p>
          <button className="btn-secondary" onClick={() => { resetExact(); tree.loadTop(1); }}>
            Retry
          </button>
        </div>
      )}

      {!tree.topLoading && tree.nodes.length === 0 && !tree.topError && (
        <div className="bom-empty">
          <p>
            {inWhereUsed
              ? 'This item is not used in any BOM.'
              : `No ${topLabel} found.`}
          </p>
        </div>
      )}

      {tree.nodes.length > 0 && displayColumns && (
        <>
          <div className="bom-grid-wrap">
            <table className="bom-grid">
              <thead>
                <tr>
                  <th className="bom-col-tree">Tree</th>
                  {displayColumns.map((c) => (
                    <th
                      key={c.key}
                      className={COST_COLUMN_KEYS.has(c.key) ? 'bom-col-cost' : undefined}
                    >
                      {c.title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleNodes.map((node) => (
                  <BomRow
                    key={node.nodeKey}
                    node={node}
                    view={view}
                    columns={displayColumns}
                    cost={costs.get(node.nodeKey)}
                    onCalcTotal={handleCalcTotal}
                    calculating={calcKeys.has(node.nodeKey)}
                    onToggle={() => tree.toggle(node)}
                    onExpandAll={() => handleExpandAll(node)}
                    expandAllProgress={progress[node.nodeKey]}
                    targetOriginalId={targetOriginalId}
                    onWhereUsed={bomActions && !noWhereUsed ? onWhereUsed : undefined}
                    onLocate={inWhereUsed ? handleLocate : undefined}
                    locating={locatingKey === node.nodeKey}
                    located={locatedKey === node.nodeKey}
                    levelNum={levelNumbers.get(node.nodeKey) || ''}
                    onExport={setExportNode}
                    onExpandByCategory={
                      EXPAND_BY_CATEGORY_ENABLED ? handleExpandByCategory : undefined
                    }
                    categoryLoading={categoryLoadingKey === node.nodeKey}
                    onRelatedEcos={bomActions ? setEcoNode : undefined}
                    onCompare={bomActions ? setCompareNode : undefined}
                    onDiscuss={handleDiscuss}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <div className="bom-status-bar">
            <div className="bom-status-info">
              {(() => {
                const rootsOnPage = tree.nodes.filter((n) => n.depth === 0).length;
                const startIdx = (tree.topPage - 1) * tree.topPageSize + 1;
                const endIdx = startIdx + rootsOnPage - 1;
                const knownTotal = tree.topTotal > tree.topPageSize ? tree.topTotal : null;
                if (visibleSet) {
                  return `Showing ${visibleNodes.length} of ${tree.nodes.length} loaded rows · page ${tree.topPage}`;
                }
                if (knownTotal) {
                  const totalPages = Math.max(1, Math.ceil(knownTotal / tree.topPageSize));
                  return `${topLabel} ${startIdx}–${endIdx} of ${knownTotal} · page ${tree.topPage}/${totalPages} · ${tree.nodes.length} total loaded rows`;
                }
                if (tree.topPage > 1 || tree.topHasMore) {
                  return `${topLabel} ${startIdx}–${endIdx} · page ${tree.topPage} · ${tree.nodes.length} total loaded rows`;
                }
                return `${rootsOnPage} ${topLabel} · ${tree.nodes.length} total loaded rows`;
              })()}
            </div>
            {(() => {
              const knownTotal = tree.topTotal > tree.topPageSize ? tree.topTotal : null;
              const totalPages = knownTotal ? Math.max(1, Math.ceil(knownTotal / tree.topPageSize)) : null;
              const hasNext = totalPages
                ? tree.topPage < totalPages
                : tree.topHasMore;
              if (!totalPages && !tree.topHasMore && tree.topPage <= 1) return null;
              return (
                <div className="bom-pagination">
                  {totalPages && (
                    <button
                      type="button"
                      onClick={() => { resetExact(); tree.loadTop(1); }}
                      disabled={tree.topPage <= 1 || tree.topLoading}
                      aria-label="First page"
                    >‹‹</button>
                  )}
                  <button
                    type="button"
                    onClick={() => { resetExact(); tree.loadTop(tree.topPage - 1); }}
                    disabled={tree.topPage <= 1 || tree.topLoading}
                    aria-label="Previous page"
                  >‹ Prev</button>
                  <span className="bom-pagination-info">
                    {totalPages
                      ? `${tree.topPage} / ${totalPages}`
                      : `Page ${tree.topPage}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => { resetExact(); tree.loadTop(tree.topPage + 1); }}
                    disabled={!hasNext || tree.topLoading}
                    aria-label="Next page"
                  >Next ›</button>
                  {totalPages && (
                    <button
                      type="button"
                      onClick={() => { resetExact(); tree.loadTop(totalPages); }}
                      disabled={tree.topPage >= totalPages || tree.topLoading}
                      aria-label="Last page"
                    >››</button>
                  )}
                </div>
              );
            })()}
          </div>
        </>
      )}
      {exportNode && (
        <ExportModal
          node={exportNode}
          columns={columns || []}
          treeView={view}
          onClose={() => setExportNode(null)}
        />
      )}
      {categoryPicker && (
        <CategoryModal
          itemName={
            categoryPicker.node.row.objName ||
            categoryPicker.node.row.userPrefix ||
            'Item'
          }
          columnTitle={categoryPicker.col.title || 'Category'}
          options={categoryPicker.options}
          onCancel={() => setCategoryPicker(null)}
          onConfirm={confirmExpandByCategory}
        />
      )}
      {ecoNode && (
        <EcoModal
          row={ecoNode.row}
          onClose={() => setEcoNode(null)}
          onAuthExpired={onAuthExpired}
        />
      )}
      {compareNode && (
        <CompareModal
          row={compareNode.row}
          getChildren={() => tree.getChildRows(compareNode)}
          onClose={() => setCompareNode(null)}
          onAuthExpired={onAuthExpired}
        />
      )}
      {/* Portalled to the body: the menu is `position: fixed`, and it opens from a row
          inside a scrolling table. Its styles are the platform's, not the module's, so it
          deliberately does NOT carry a `.bom-scope` wrapper. */}
      {discussAt && createPortal(
        <DiscussMenu
          at={discussAt}
          context={discussAt.context}
          onClose={() => setDiscussAt(null)}
        />,
        document.body
      )}
    </div>
  );
}
